import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { goWebApp, webAppGalleryUrl, webAppLoginUrl } from '../utils/webAppUrl';
import { BookOpen, ChevronDown } from 'lucide-react';
import { useAppSelector } from '../hooks/redux';
import gnbLogo from '../assets/images/GNB_logo.svg';
import { LandingMobileNavMenu } from './LandingMobileNavMenu';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLandingHeaderNavCollapse } from '../hooks/useLandingHeaderNavCollapse';
import { isLandingStylePath } from '../utils/landingRoutes';
import { LocalizedLink, useLocalizedPath } from '../i18n/LocalizedLink';
import { stripLocalePrefix } from '../i18n/localeRouting';

type HeaderDropdownId = 'service' | 'use-cases' | 'blog';

type HeaderSubmenuItem = {
  label: string;
  to: string;
};

const Header: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const localize = useLocalizedPath();
  const { pathname } = useLocation();
  const barePath = stripLocalePrefix(pathname);
  const isLandingPage = barePath === '/';
  const isBrandStoryPage = barePath === '/brand-story';
  const isLandingStylePage = isLandingStylePath(pathname);
  const isServiceNavActive = barePath.startsWith('/service');
  const isUseCasesNavActive = barePath.startsWith('/use-cases');
  const isBlogNavActive = barePath.startsWith('/blog');
  const isContactNavActive = barePath.startsWith('/contact');
  const { accessToken, sessionId } = useAppSelector((state) => state.userInfo);
  const isLoggedIn = !!(accessToken || sessionId);
  const [openDropdown, setOpenDropdown] = useState<HeaderDropdownId | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeDropdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { headerRef, logoRef, midRef, actionsRef, isNavCollapsed } =
    useLandingHeaderNavCollapse(isLandingStylePage, i18n.language);

  const serviceSubmenu = useMemo<HeaderSubmenuItem[]>(
    () => [
      { label: t('nav.serviceFeatures'), to: '/service/features' },
      { label: t('nav.servicePricing'), to: '/service/pricing' },
      { label: t('nav.serviceComparison'), to: '/service/comparison' },
    ],
    [t]
  );

  const useCasesSubmenu = useMemo<HeaderSubmenuItem[]>(
    () => [
      { label: t('nav.useCasesParents'), to: '/use-cases/parents' },
      { label: t('nav.useCasesDaycare'), to: '/use-cases/daycare' },
      { label: t('nav.useCasesEvents'), to: '/use-cases/events' },
      { label: t('nav.useCasesSeniors'), to: '/use-cases/seniors' },
    ],
    [t]
  );

  const blogSubmenu = useMemo<HeaderSubmenuItem[]>(
    () => [
      { label: t('nav.blogInsights'), to: '/blog/insights' },
      { label: t('nav.blogUpdates'), to: '/blog/updates' },
      { label: t('nav.blogFaq'), to: '/blog/faq' },
    ],
    [t]
  );

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const toggleMobileNav = useCallback(() => setMobileNavOpen((prev) => !prev), []);

  const clearCloseDropdownTimer = () => {
    if (closeDropdownTimerRef.current) {
      clearTimeout(closeDropdownTimerRef.current);
      closeDropdownTimerRef.current = null;
    }
  };

  const openHeaderDropdown = (id: HeaderDropdownId) => {
    clearCloseDropdownTimer();
    setOpenDropdown(id);
  };

  const scheduleCloseHeaderDropdown = () => {
    clearCloseDropdownTimer();
    closeDropdownTimerRef.current = setTimeout(() => {
      setOpenDropdown(null);
      closeDropdownTimerRef.current = null;
    }, 120);
  };

  useEffect(() => () => clearCloseDropdownTimer(), []);

  useEffect(() => {
    if (isNavCollapsed) {
      setOpenDropdown(null);
    } else {
      setMobileNavOpen(false);
    }
  }, [isNavCollapsed]);

  const logoBlock = (
    <div className="top_logo">
      <LocalizedLink to="/" className="link_logo landing_header_logo_pill">
        <img className="logo" src={gnbLogo} alt="logo" />
      </LocalizedLink>
    </div>
  );

  const renderSubmenuLinks = (items: HeaderSubmenuItem[]) =>
    items.map((item) => {
      const localizedTo = localize(item.to);
      const isSubActive = pathname === localizedTo || barePath === item.to;

      return (
        <li key={item.to}>
          <LocalizedLink
            to={item.to}
            className={`landing_header_submenu_link${isSubActive ? ' is-active' : ''}`}
            aria-current={isSubActive ? 'page' : undefined}
          >
            {item.label}
          </LocalizedLink>
        </li>
      );
    });

  const renderDropdownNavItem = (
    id: HeaderDropdownId,
    parentLink: React.ReactNode,
    items: HeaderSubmenuItem[]
  ) => (
    <li
      className={`landing_header_nav_item landing_header_nav_item--has-submenu${
        openDropdown === id ? ' is-submenu-open' : ''
      }`}
      onMouseEnter={() => openHeaderDropdown(id)}
      onMouseLeave={scheduleCloseHeaderDropdown}
      onFocus={() => openHeaderDropdown(id)}
      onBlur={scheduleCloseHeaderDropdown}
    >
      {parentLink}
      <div
        className={`landing_header_submenu_dropdown landing_header_submenu_dropdown--${id}${
          openDropdown === id ? ' is-open' : ''
        }`}
        aria-hidden={openDropdown !== id}
        onMouseEnter={() => openHeaderDropdown(id)}
      >
        <ul className="landing_header_submenu" aria-label={t('nav.service')}>
          {openDropdown === id ? renderSubmenuLinks(items) : null}
        </ul>
      </div>
    </li>
  );

  const landingMidNav = isLandingStylePage && (
    <ul ref={midRef} className="landing_header_mid" aria-label={t('nav.service')}>
      <li>
        <LocalizedLink
          to="/brand-story"
          className={`landing_header_mid_link${isBrandStoryPage ? ' is-active' : ''}`}
          aria-current={isBrandStoryPage ? 'page' : undefined}
        >
          {t('nav.brandStory')}
        </LocalizedLink>
      </li>
      {renderDropdownNavItem(
        'service',
        <LocalizedLink
          to="/service/features"
          className={`landing_header_mid_link landing_header_mid_link--dropdown${
            isServiceNavActive || openDropdown === 'service' ? ' is-active' : ''
          }`}
          aria-current={isServiceNavActive ? 'page' : undefined}
          aria-expanded={openDropdown === 'service'}
          aria-haspopup="true"
        >
          <span>{t('nav.service')}</span>
          <ChevronDown className="landing_header_mid_chevron" strokeWidth={2.25} aria-hidden />
        </LocalizedLink>,
        serviceSubmenu
      )}
      {renderDropdownNavItem(
        'use-cases',
        <LocalizedLink
          to="/use-cases"
          className={`landing_header_mid_link landing_header_mid_link--dropdown${
            isUseCasesNavActive || openDropdown === 'use-cases' ? ' is-active' : ''
          }`}
          aria-current={isUseCasesNavActive ? 'page' : undefined}
          aria-expanded={openDropdown === 'use-cases'}
          aria-haspopup="true"
        >
          <span>{t('nav.useCases')}</span>
          <ChevronDown className="landing_header_mid_chevron" strokeWidth={2.25} aria-hidden />
        </LocalizedLink>,
        useCasesSubmenu
      )}
      {renderDropdownNavItem(
        'blog',
        <LocalizedLink
          to="/blog/insights"
          className={`landing_header_mid_link landing_header_mid_link--dropdown${
            isBlogNavActive || openDropdown === 'blog' ? ' is-active' : ''
          }`}
          aria-current={isBlogNavActive ? 'page' : undefined}
          aria-expanded={openDropdown === 'blog'}
          aria-haspopup="true"
        >
          <span>{t('nav.blog')}</span>
          <ChevronDown className="landing_header_mid_chevron" strokeWidth={2.25} aria-hidden />
        </LocalizedLink>,
        blogSubmenu
      )}
      <li>
        <LocalizedLink
          to="/contact"
          className={`landing_header_mid_link${isContactNavActive ? ' is-active' : ''}`}
          aria-current={isContactNavActive ? 'page' : undefined}
        >
          {t('nav.contact')}
        </LocalizedLink>
      </li>
      <li>
        <a
          href={webAppGalleryUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="landing_header_mid_link"
        >
          {t('nav.gallery')}
        </a>
      </li>
    </ul>
  );

  const mobileNavMenu = isLandingStylePage ? (
    <LandingMobileNavMenu
      isOpen={mobileNavOpen}
      isEnabled={isNavCollapsed}
      onToggle={toggleMobileNav}
      onClose={closeMobileNav}
      serviceItems={serviceSubmenu}
      useCaseItems={useCasesSubmenu}
      blogItems={blogSubmenu}
    />
  ) : null;

  const actionsBlock = (
    <div className="top_btnArea">
      <ul className="tracking-wide medium flex flex-row landing_header_actions_list items-center">
        <li className="landing_header_lang_item">
          <LanguageSwitcher />
        </li>
        {isLoggedIn ? (
          <li className="bookcreation">
            <button
              type="button"
              onClick={() => goWebApp('/book-creation')}
              className="btn_bookcreation landing_header_btn_book"
            >
              <BookOpen size={18} strokeWidth={1.85} aria-hidden />
              <span>{t('nav.createBook')}</span>
            </button>
          </li>
        ) : (
          <li className="mybookshelf">
            <a href={webAppLoginUrl()} className="btn_mybookshelf">
              {t('nav.login')}
            </a>
          </li>
        )}
        {mobileNavMenu}
      </ul>
    </div>
  );

  if (isLandingStylePage) {
    return (
      <header
        ref={headerRef}
        className={`main_header main_header--landing landing_header_bar landing_header_bar--home${
          isLandingPage ? '' : ' landing_header_bar--subpage'
        }${
          openDropdown ? ` landing_header_bar--submenu-open landing_header_bar--submenu-${openDropdown}` : ''
        }${mobileNavOpen ? ' landing_header_bar--mobile-nav-open' : ''}${
          isNavCollapsed ? ' landing_header_bar--nav-collapsed' : ''
        }`}
        onMouseLeave={scheduleCloseHeaderDropdown}
      >
        <div ref={logoRef} className="landing_header_cell landing_header_cell--logo">
          {logoBlock}
        </div>
        <div className="landing_header_cell landing_header_cell--mid">{landingMidNav}</div>
        <div ref={actionsRef} className="landing_header_cell landing_header_cell--actions">
          {actionsBlock}
        </div>
      </header>
    );
  }

  return (
    <header className="main_header">
      <nav>
        {logoBlock}
        {actionsBlock}
      </nav>
    </header>
  );
};

export default Header;
